import { useState } from "react";

import { Button, Callout, Classes } from "@blueprintjs/core";

import { RichContentRenderer } from "@/components/helpers/ui/RichContentRenderer";
import { Box } from "@/components/layout/flexbox";
import {
  buildComponentMetadata,
  isComponentInProgressOrOverdue,
} from "@/components/pages_logged_in/program/components/componentMetadata";
import { ParticipantComponentStatistic } from "@/components/pages_logged_in/program/components/program-component/ParticipantComponentStatistic";
import { UrlLink } from "@/components/pages_logged_in/program/components/program-component/UrlLink";
import { SubmitActionItemDialog } from "@/components/pages_logged_in/program/components/SubmitActionItemDialog";
import { canViewComponentStatistic } from "@/components/pages_logged_in/program/components/utils";
import { LinkPreview } from "@/components/pieces/link/LinkPreview";
import { ClampedTextWithToggle } from "@/components/pieces/products/ComponentInfo";
import { VideoEmbed } from "@/components/pieces/videoEmbed";
import {
  ProgramComponentsQueryDocument,
  ProgramSurveyQueryDocument,
  useSaveProgramSurveyAnswerMutation,
} from "@/graphql";
import { cn } from "@/lib/utils";
import { PanelProps } from "@/types/programs";
import { ensureNonNullArray } from "@/utils/array";

export function ProgramVideosAndArticlesItemPanel({
  component,
  program,
  role = "participant",
  large = false,
  view = "list",
}: PanelProps) {
  const { participantGuidance, status } = component || {};
  const isProgramLead = role === "lead";

  const canViewStatistic = canViewComponentStatistic(component, isProgramLead);

  const metaData = buildComponentMetadata({
    component,
    isProgramLead,
  });
  const formFields = ensureNonNullArray(component?.survey?.formFields);

  const [errors, setErrors] = useState<string[]>([]);

  const [saveProgramSurveyAnswer, { loading: savePending }] =
    useSaveProgramSurveyAnswerMutation({
      onCompleted: (data) => {
        const ok = data?.saveProgramSurveyAnswer?.ok;
        if (ok) {
          setOpenConfirmDialog(false);
        } else {
          const message = data?.saveProgramSurveyAnswer?.errors?.[0]?.message;
          setErrors([message || "Something went wrong. Please try again."]);
        }
      },
      onError: (err) => {
        setErrors([err?.message || "Cannot save Program Context"]);
      },
      awaitRefetchQueries: true,
      refetchQueries: [
        {
          query: ProgramSurveyQueryDocument,
          variables: { programSurveyId: component?.survey?.id },
        },
        {
          query: ProgramComponentsQueryDocument,
          variables: { programId: program?.id },
        },
      ],
    });

  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);

  const onSubmitAnswerHandler = (fieldId: string, value: string) => {
    if (!component.survey?.id) {
      setErrors(["Survey not found"]);
      return;
    }
    saveProgramSurveyAnswer({
      variables: {
        surveyId: component.survey.id,
        answerData: {
          fieldId,
          value,
        },
      },
    });
  };

  const mainFormField = formFields[0];
  const url = mainFormField?.link?.url || "";
  const urlText = mainFormField?.link?.text || "";
  const isCompletable =
    !isProgramLead && isComponentInProgressOrOverdue(status);

  if (view === "feed") {
    return (
      <div
        className="xs:p-3.5 xs:rounded-md xs:gap-3 flex flex-col md:gap-4 md:rounded-lg md:p-6"
        style={{
          background: "linear-gradient(127deg, #F2FAFF 7.13%, #D8ECF8 102.77%)",
        }}
      >
        <div className="text-lg font-semibold text-blue-800">
          {metaData.isArticle
            ? "You have an article to read"
            : "You have a video to watch"}
        </div>
        {!!participantGuidance && (
          <div className={Classes.RUNNING_TEXT}>
            <ClampedTextWithToggle
              className="xs:text-sm font-normal text-gray-700 md:text-base"
              text={participantGuidance}
              lines={2}
            />
          </div>
        )}
        {metaData.isVideo && url && (
          <VideoEmbed
            url={url}
            altComponent={<UrlLink url={url} text={urlText} />}
          />
        )}
        {metaData.isArticle && url && (
          <LinkPreview
            url={url}
            isEditable={false}
            metadata={
              mainFormField?.link
                ? {
                    text: mainFormField.link.text || url,
                    imageSrc: mainFormField.link.imageSrc,
                  }
                : null
            }
          />
        )}
        {!url && (
          <Callout intent="danger" icon="warning-sign">
            No content available
          </Callout>
        )}
      </div>
    );
  }

  return (
    <Box className="xs:gap-3.5 flex flex-col md:gap-4 empty:hidden">
      <SubmitActionItemDialog
        open={openConfirmDialog}
        onClose={() => {
          setOpenConfirmDialog(false);
        }}
        onConfirm={() => {
          if (!component.survey?.id) {
            setErrors(["Survey not found"]);
            return;
          }
          saveProgramSurveyAnswer({
            variables: {
              surveyId: component.survey.id,
            },
          });
        }}
      />

      {canViewStatistic && (
        <ParticipantComponentStatistic
          component={component}
          program={program}
          isProgramLead={isProgramLead}
          hideStarted={true}
        />
      )}

      {!!participantGuidance && (
        <div className="flex flex-col gap-2">
          {metaData.isSurvey && (
            <div className="text-subtext text-xs font-semibold">GUIDANCE</div>
          )}
          <Box className={Classes.RUNNING_TEXT}>
            <RichContentRenderer content={participantGuidance} />
          </Box>
        </div>
      )}
      <div
        className={cn("xs:gap-3.5 flex flex-1 flex-col md:gap-4", {
          "max-w-180": !large,
        })}
      >
        {metaData.isVideo && url && (
          <VideoEmbed
            url={url}
            altComponent={<UrlLink url={url} text={urlText} />}
          />
        )}
        {metaData.isArticle && url && (
          <LinkPreview
            url={url}
            isEditable={false}
            metadata={
              mainFormField?.link
                ? {
                    text: mainFormField.link.text || url,
                    imageSrc: mainFormField.link.imageSrc,
                  }
                : null
            }
          />
        )}
        {!url && (
          <Callout intent="danger" icon="warning-sign">
            No content available
          </Callout>
        )}
        {isCompletable && metaData.isEligible && (
          <Box>
            <Button
              intent="primary"
              text="Mark Complete"
              large={true}
              loading={savePending}
              disabled={savePending}
              onClick={() => {
                onSubmitAnswerHandler(mainFormField.id, `true`);
              }}
            />
          </Box>
        )}
        {errors.length > 0 && (
          <div className="flex flex-col gap-2">
            {errors.map((error, index) => (
              <Callout key={index} intent="danger" icon="warning-sign">
                {error}
              </Callout>
            ))}
          </div>
        )}
      </div>
    </Box>
  );
}
